import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, subDays } from 'date-fns'
import { RefreshCw } from 'lucide-react'
import { paymentsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import { Button } from '../components/ui'
import DeclinesSection from '../components/DeclinesSection'
import DeclineBreakdownChart from '../components/DeclineBreakdownChart'
import DeclineTrendsChart from '../components/DeclineTrendsChart'
import DeclineSourceSection from '../components/DeclineSourceSection'
import DeclineReasonRates from '../components/DeclineReasonRates'

const currency = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
const count = value => Number(value || 0).toLocaleString('en-US')
const controlClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground'

export default function DeclinesPage() {
  const { currentClinicId, clinics } = useAuthStore()
  const [dateRange, setDateRange] = useState(() => ({ from: subDays(new Date(), 29), to: new Date() }))
  const [refresh, setRefresh] = useState(0)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  // Kept per clinic, so switching clinic drops a MID that belongs to another.
  const [midChoice, setMidChoice] = useState({ clinic: null, mid: '' })
  const mid = midChoice.clinic === currentClinicId ? midChoice.mid : ''
  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')
  const requestKey = `${currentClinicId}:${startDate}:${endDate}:${mid}:${refresh}`
  const data = result?.key === requestKey ? result.data : null

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    if (!currentClinicId) { setLoading(false); return }
    paymentsAPI.getDeclines({ startDate, endDate, ...(mid ? { mid } : {}) })
      .then(response => { if (active) setResult({ key: requestKey, data: response.data.data }) })
      .catch(err => { if (active) setError(err.response?.data?.error?.message || 'Could not load declines. Try refreshing.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [currentClinicId, startDate, endDate, mid, requestKey])

  // The list comes back unfiltered with every response, so the last one for
  // this clinic still fills the selector while the next request loads.
  const lastData = result?.key?.startsWith(`${currentClinicId}:`) ? result.data : null
  const mids = lastData?.mids || []

  return <div className="flex flex-col gap-6 p-4 sm:p-6">
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-1">
        <Link to="/ops/payments" className="text-sm text-muted-foreground hover:text-foreground">Payments</Link>
        <h1 className="text-2xl font-bold">Declines</h1>
        <p className="text-sm text-muted-foreground">{clinics?.find(clinic => clinic.id === currentClinicId)?.name || currentClinicId || 'Select a clinic'} · Payment outcomes, retries, and reasons.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(mids.length > 1 || mid) && (
          <select
            className={controlClass}
            aria-label="Merchant account"
            value={mid}
            onChange={event => setMidChoice({ clinic: currentClinicId, mid: event.target.value })}
          >
            <option value="">All MIDs</option>
            {mids.map(option => (
              <option key={option.id} value={option.id}>{option.name} ({count(option.attempts)})</option>
            ))}
          </select>
        )}
        <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
        <Button variant="outline" size="sm" aria-label="Refresh declines" disabled={loading || !currentClinicId} onClick={() => setRefresh(value => value + 1)}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>
    </header>
    {!currentClinicId ? <p className="text-sm text-muted-foreground">Select a clinic to view its declines.</p> : <>
      <p className="text-xs text-muted-foreground">{startDate} through {endDate} · Eastern time{data?.mid ? ` · ${data.mid.name} only` : ''}. Acceptance counts people; the charts below count payment attempts.</p>
      <DeclinesSection data={data} loading={loading} error={error} formatCurrency={currency} formatNumber={count} />
      {data && !error && !loading && data.totals?.attempted > 0 && <>
        <DeclineTrendsChart key={requestKey} daily={data.series} weekly={data.weeklySeries} />
        <DeclineReasonRates data={data.reasonRates} formatNumber={count} />
        <DeclineSourceSection rows={data.breakdowns?.source} coverage={data.sourceCoverage} formatCurrency={currency} formatNumber={count} />
      </>}
      {data && !error && !loading && <section className="space-y-4" aria-label="Decline breakdowns">
        <div><h2 className="text-lg font-semibold">Where payments fail</h2><p className="mt-1 text-sm text-muted-foreground">Compare decline volume with decline rate. Small groups can have high rates on very few attempts; open the count tables for context. Missing card data stays in “Not reported / not applicable,” including BNPL. Apple and Google Pay report their card network but never a real BIN (only the phone token’s), so wallets always sit in “Not reported” for BIN.</p></div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <DeclineBreakdownChart title="By card brand" rows={data.breakdowns?.network} />
          <DeclineBreakdownChart title="By payment method" rows={data.breakdowns?.method} />
          <DeclineBreakdownChart title="By BIN" rows={data.breakdowns?.bin} isBin />
          <DeclineBreakdownChart title="By processor" rows={data.breakdowns?.connector} />
        </div>
      </section>}
    </>}
  </div>
}
